import Spinner from "../../../Spinners";
import { Panel } from "../../../Panel";

/**
 * Days between the invoice email going out and the card being charged.
 *
 * Mirrors the backend's `SCHEDULED_CHARGE_NOTICE_DAYS`
 * (`src/modules/admin/commission-billing/constants/billing.constants.ts`),
 * which is what actually schedules the charge. This number is the promise the
 * copy below makes, so the two only ever move together — never inline the
 * literal into a sentence.
 */
export const INVOICE_NOTICE_DAYS = 5;

interface BillingMethodCardProps {
  prefersEmailInvoice: boolean;
  /** False when we can't bill this account on the emailed-invoice schedule (no
   *  Stripe customer or no billing configuration yet) — the choice is shown but
   *  not offered, rather than accepting a preference billing would ignore. */
  canUseEmailInvoice: boolean;
  isLoading: boolean;
  isSaving: boolean;
  onChange: (prefersEmailInvoice: boolean) => void;
}

/**
 * When the customer's card gets charged — the moment the month is billed, or a
 * few days after they've been emailed the invoice (AUT-136).
 *
 * BOTH options charge the same saved card, and nothing about WHAT is billed
 * changes here: the choice is only whether the invoice arrives before the money
 * moves. That distinction is easy to misread as "pay it yourself", which is
 * exactly what this surface must not imply — there is no hosted payment page
 * and no manual step in either mode. Presented as two radio cards rather than a
 * switch because the consequence of each is a sentence, not a state.
 */
export function BillingMethodCard({
  prefersEmailInvoice,
  canUseEmailInvoice,
  isLoading,
  isSaving,
  onChange,
}: BillingMethodCardProps) {
  const disabled = isLoading || isSaving;

  const options = [
    {
      value: false,
      title: "Charge my card on file",
      detail:
        "We bill the saved card automatically when your monthly invoice is ready.",
      available: true,
    },
    {
      value: true,
      title: "Email my invoice first",
      detail: canUseEmailInvoice
        ? `We email your invoice at the start of the month, then charge the same card on file ${INVOICE_NOTICE_DAYS} days later — so you have time to review it. There's still nothing to pay by hand: we take it automatically and send your receipt.`
        : "We need a saved card and an active billing setup before we can send your invoice ahead of the charge. Add a payment method above to turn this on.",
      available: canUseEmailInvoice,
    },
  ];

  return (
    <Panel
      label={
        <div className="flex items-center gap-2">
          <span className="text-content-primary text-lg font-medium leading-normal">
            Billing Method
          </span>
          {isSaving && <Spinner className="!text-primary !w-4 !h-4" />}
        </div>
      }
      className="mb-6"
    >
      <div className="p-4 flex flex-col gap-2">
        {isLoading ? (
          <div className="animate-pulse flex flex-col gap-2">
            <div className="h-14 bg-gray-200 rounded" />
            <div className="h-14 bg-gray-200 rounded" />
          </div>
        ) : (
          options.map((option) => {
            const selected = prefersEmailInvoice === option.value;
            const selectable = option.available && !disabled;
            return (
              <label
                key={String(option.value)}
                className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${
                  selected
                    ? "border-primary bg-primary/5"
                    : "border-border-secondary"
                } ${selectable ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
              >
                <input
                  type="radio"
                  name="billing-method"
                  className="mt-0.5"
                  checked={selected}
                  disabled={!selectable}
                  onChange={() => {
                    if (!selectable || selected) return;
                    onChange(option.value);
                  }}
                />
                <span className="text-content-primary font-medium">
                  {option.title}
                  <span className="block text-xs font-normal text-content-secondary">
                    {option.detail}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </Panel>
  );
}
